// AuthChannel abstraction — pluggable OTP delivery.
//
// Purpose: WhatsApp OTP will likely replace SMS as the primary delivery
// channel once Meta business verification clears, and the switch should
// be a config flag flip + one new implementation — no call-site changes.
// This module is that seam.
//
// The abstraction test the brief calls out (session-1-firebase-v9-migration.md):
// "adding WhatsApp later should mean writing one new implementation and
// flipping a config value — not touching any call site that invokes
// auth." If Firebase-specific types (`ConfirmationResult`,
// `RecaptchaVerifier`) leak into calling code, the abstraction has
// failed.
//
// Interface (pseudo — JS has no interfaces, contract is by convention):
//   channelName: 'sms' | 'whatsapp'
//   sendCode(phoneNumber: string, uiHostElement: HTMLElement)
//     -> Promise<{ verificationId: string }>
//   verifyCode(verificationId: string, code: string)
//     -> Promise<UserCredential>   // Firebase UserCredential — same shape
//                                  // both channels return (Firebase can
//                                  // sign-in-with-credential from any
//                                  // provider), so this doesn't force
//                                  // callers to know which channel ran.
//   reset() -> void
//     Called on error or after successful sign-in — releases the
//     internal reCAPTCHA container (SMS only) so the next attempt
//     starts fresh. Safe to call on WhatsApp stub (no-op).
//
// Callers MUST NOT import SmsAuthChannel / WhatsAppAuthChannel directly
// — always route through getAuthChannel(featuresConfig). The factory is
// the one place that knows about channel selection.

import {
    RecaptchaVerifier,
    signInWithPhoneNumber,
    PhoneAuthProvider,
    signInWithCredential,
} from 'firebase/auth';
import { auth } from './firebase.js';

// Local isNative() helper (deliberately duplicated from native-shell.js
// to keep this module dependency-light — it only imports firebase primitives).
function _isNative() {
    return !!(
        typeof window !== 'undefined' &&
        window.Capacitor &&
        typeof window.Capacitor.isNativePlatform === 'function' &&
        window.Capacitor.isNativePlatform()
    );
}


export class SmsAuthChannel {
    channelName = 'sms';

    // Private: RecaptchaVerifier is a Firebase-specific type. Held as
    // instance state so the caller never sees it. Recreated per sendCode
    // when needed (the verifier gets consumed by signInWithPhoneNumber
    // and can't be reused cleanly across sends).
    #recaptchaVerifier = null;

    /**
     * Send an SMS verification code.
     * @param {string} phoneNumber E.164 format (e.g. +2348012345678)
     * @param {HTMLElement} uiHostElement DOM element to host the
     *   invisible reCAPTCHA widget. Firebase mounts on it directly.
     *   Typical usage: a hidden <div id="recaptcha-container"></div>
     *   somewhere in the signup form.
     * @returns {Promise<{verificationId: string}>}
     */
    async sendCode(phoneNumber, uiHostElement) {
        // Invisible reCAPTCHA — user only sees a challenge if Firebase's
        // risk scoring escalates (traffic spike from an IP range, etc.).
        // For real users on the phone signup path this is transparent.
        this.reset();
        this.#recaptchaVerifier = new RecaptchaVerifier(auth, uiHostElement, {
            size: 'invisible',
        });

        const confirmation = await signInWithPhoneNumber(
            auth,
            phoneNumber,
            this.#recaptchaVerifier
        );
        // confirmation is a ConfirmationResult — Firebase-specific type
        // we deliberately do NOT return. Callers get a plain string
        // verificationId that we hand back to verifyCode() below.
        return { verificationId: confirmation.verificationId };
    }

    /**
     * Complete phone sign-in with the code the user typed in.
     * @param {string} verificationId The id returned from sendCode.
     * @param {string} code 6-digit code the user received via SMS.
     * @returns {Promise<import('firebase/auth').UserCredential>}
     */
    async verifyCode(verificationId, code) {
        const credential = PhoneAuthProvider.credential(verificationId, code);
        const result = await signInWithCredential(auth, credential);
        // Sign-in complete; reCAPTCHA container is no longer needed.
        this.reset();
        return result;
    }

    /**
     * Clear the internal reCAPTCHA state. Call on error paths so a
     * retry gets a fresh verifier — reusing a consumed one produces
     * "reCAPTCHA has already been rendered in this element" errors.
     */
    reset() {
        if (this.#recaptchaVerifier) {
            try {
                this.#recaptchaVerifier.clear();
            } catch (_) {
                // Already cleared or never fully rendered — safe to ignore.
            }
            this.#recaptchaVerifier = null;
        }
    }
}


// Session 2b Phase 1 (Task 3b): native phone auth via Play Integrity.
//
// RecaptchaVerifier loads gstatic scripts at runtime — unreliable inside a
// Capacitor WebView. Native phone auth uses Play Integrity through the
// Capawesome @capacitor-firebase/authentication plugin instead: Google's
// on-device attestation replaces the reCAPTCHA challenge, no gstatic load.
//
// Requires the SHA-1 fingerprint registered on the Firebase Android app
// (already done for dev + prod as of 2026-09-15; staging is phone-auth-
// enabled with the same SHA-1 registered even though Google Sign-In is
// skipped there — see memory `Google Sign-In not enabled on staging Android`).
//
// The AuthChannel seam absorbs this cleanly: the shape of sendCode +
// verifyCode is unchanged, only the underlying Firebase primitive differs.
// Caller (app.js phone-signup-modal handler) doesn't need to know which
// implementation ran. Return values are approximate on the native path
// (no full UserCredential — caller reads auth.currentUser after success)
// but the caller doesn't inspect the return value, so this is fine.
export class NativeSmsAuthChannel {
    channelName = 'sms-native';

    #verificationId = null;

    async sendCode(phoneNumber, _uiHostElement) {
        // Dynamic import so the Capawesome plugin isn't pulled into the web
        // bundle. Vite treats this as a code-split; on native the module is
        // present at build time. On web this line would throw at runtime,
        // but getAuthChannel() prevents web from constructing this class.
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber });
        // On success Android auto-verify path, `signIn` field is set and the
        // user is already authenticated — in that case verificationId may be
        // absent. Caller's verifyCode will then be a no-op because auth state
        // already flipped. Explicit check to surface the auto-verify case.
        if (result.signIn) {
            this.#verificationId = null;
            return { verificationId: null, autoVerified: true };
        }
        this.#verificationId = result.verificationId;
        return { verificationId: result.verificationId };
    }

    async verifyCode(verificationId, code) {
        // Auto-verify short-circuit: if the Android SMS Retriever API pulled
        // the code automatically during sendCode, verificationId is null and
        // we already have an authenticated user. Return current state.
        if (!verificationId) {
            return { user: auth.currentUser };
        }
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        await FirebaseAuthentication.confirmVerificationCode({
            verificationId,
            verificationCode: code,
        });
        // Plugin's default `skipNativeAuth: false` also authenticates the JS
        // Firebase SDK; auth.currentUser is now populated + onAuthStateChanged
        // fires. Return approximate UserCredential shape for symmetry with
        // SmsAuthChannel (caller ignores this today, but symmetry protects
        // against a future caller who does read it).
        this.#verificationId = null;
        return { user: auth.currentUser };
    }

    reset() {
        this.#verificationId = null;
    }
}


export class WhatsAppAuthChannel {
    channelName = 'whatsapp';

    async sendCode(_phoneNumber, _uiHostElement) {
        throw new Error(
            'WhatsApp auth channel not yet implemented — pending Meta business verification. ' +
            'Set FEATURES.authChannel = "sms" or leave it unset (default is "sms").'
        );
    }

    async verifyCode(_verificationId, _code) {
        throw new Error('WhatsApp auth channel not yet implemented');
    }

    reset() {
        // No state to clear on the stub.
    }
}


/**
 * Factory — returns the configured AuthChannel implementation.
 *
 * Selection is driven by `featuresConfig.authChannel`. Currently:
 *   'sms'      -> SmsAuthChannel (works today)
 *   'whatsapp' -> WhatsAppAuthChannel (stub, throws)
 *   anything else / missing -> default to 'sms'
 *
 * Do not import SmsAuthChannel / WhatsAppAuthChannel from anywhere else.
 * All call sites go through this factory so a future config flip is the
 * only touchpoint.
 *
 * @param {{authChannel?: 'sms' | 'whatsapp'}} featuresConfig
 * @returns {SmsAuthChannel | WhatsAppAuthChannel}
 */
export function getAuthChannel(featuresConfig) {
    const channelName = (featuresConfig && featuresConfig.authChannel) || 'sms';
    switch (channelName) {
        case 'whatsapp':
            return new WhatsAppAuthChannel();
        case 'sms':
        default:
            // Session 2b Phase 1: platform branch. Web + non-Capacitor runs
            // the reCAPTCHA-backed SmsAuthChannel; native Android runs the
            // Play Integrity-backed NativeSmsAuthChannel via Capawesome
            // plugin. Both satisfy the same interface — the seam absorbs
            // the split without touching any caller.
            return _isNative() ? new NativeSmsAuthChannel() : new SmsAuthChannel();
    }
}
